"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { copyCrumbPath } from "@/lib/copy/site";

/** Linked breadcrumb derived from the current pathname — `/office`,
 *  `/office/pricing`, `/office/pricing/history` render as
 *  `office / pricing / history` with every segment except the
 *  last linked to its level.
 *
 *  Replaces both the manual `eyebrow` prop and the per-page "back"
 *  link on `PageHeading`: upward navigation is implicit in the
 *  breadcrumb, so there's nothing to maintain at the call site. */
/**
 * @param currentLabel Verbatim display for the LAST segment, overriding the
 *   slug transform — for dynamic segments like a SKU
 *   (`7artisans-12mm-t2-9-vision`) that shouldn't have hyphens turned into
 *   spaces. Rendered `normal-case` so its real casing survives.
 */
type Crumb = { label: string; href: string; verbatim: boolean };

/** A crumb a page names itself: the label verbatim (a resolved name in
 *  place of an id) and the page it opens. */
export type NamedCrumb = { label: string; href: string };

export function Breadcrumb({
  currentLabel,
  crumbs: given,
}: {
  currentLabel?: string;
  /** The whole trail, named by the page: for a tree whose URL segments
   *  are ids, or hold levels that have no page of their own (the studio's
   *  `seasons` and `episodes`). Replaces the path-derived crumbs. */
  crumbs?: readonly NamedCrumb[];
}) {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0 && !given) return null;

  // Default: one crumb per path segment. UUID segments (e.g.
  // /office/items/<uuid>) keep their hyphens; other slugs like
  // "pricing-history" turn hyphens into spaces.
  let crumbs: Crumb[] = segments.map((seg, i) => {
    const href = "/" + segments.slice(0, i + 1).join("/");
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        seg,
      );
    return {
      label: isUuid ? seg : seg.replace(/-/g, " "),
      href,
      verbatim: isUuid,
    };
  });

  // Copy console: a flat page key (e.g. `rentals-item`) is really a nested
  // tree path. Expand it into verbatim, `/`-delimited crumbs (`rentals /
  // [item]`) from the site tree, rather than flattening the hyphen to a space.
  if (
    segments[0] === "office" &&
    segments[1] === "copy" &&
    segments.length === 3
  ) {
    const tail = copyCrumbPath(segments[2]);
    if (tail.length > 0) {
      crumbs = [
        { label: "office", href: "/office", verbatim: false },
        { label: "copy", href: "/office/copy", verbatim: false },
        ...tail.map((n) => ({
          label: n.label,
          href: `/office/copy/${n.key}`,
          verbatim: true,
        })),
      ];
    }
  }

  // Docs reader: the segments between /office/library and the file are docs/
  // subdirectories (guides, rentals, …) with no page of their own, so their
  // crumbs point back at the index instead of a 404.
  if (
    segments[0] === "office" &&
    segments[1] === "docs" &&
    segments.length > 3
  ) {
    crumbs = crumbs.map((c, i) =>
      i >= 2 && i < crumbs.length - 1 ? { ...c, href: "/office/library" } : c,
    );
  }

  // The reservation id segment is a short code (reference, e.g. "R-HNVFLF")
  // or a UUID — render it verbatim, never hyphen-spaced into "R HNVFLF".
  if (
    segments[0] === "office" &&
    segments[1] === "reservations" &&
    segments.length > 2
  ) {
    crumbs = crumbs.map((c, i) =>
      i === 2 ? { ...c, label: segments[2], verbatim: true } : c,
    );
  }

  // Catalog item pages: /office/items/<sku>[/assets] — segments[2] is the
  // item's SKU slug (e.g. "ilme-fx6"), rendered verbatim, not hyphen-spaced
  // into "ilme fx6". The literal sub-pages (create, import) keep the default.
  if (
    segments[0] === "office" &&
    segments[1] === "items" &&
    segments.length > 2 &&
    segments[2] !== "create" &&
    segments[2] !== "import"
  ) {
    crumbs = crumbs.map((c, i) =>
      i === 2 ? { ...c, label: segments[2], verbatim: true } : c,
    );
  }

  // Reservation inspections and quotes: /office/reservations/<id>/inspections/<dir>,
  // /office/reservations/<id>/quotes/<quoteId> — neither level has a page of
  // its own (the panel and the table live on the reservation), so its crumb
  // points back at the reservation.
  if (
    segments[0] === "office" &&
    segments[1] === "reservations" &&
    (segments[3] === "inspections" || segments[3] === "quotes")
  ) {
    crumbs = crumbs.map((c, i) =>
      i === 3 ? { ...c, href: `/office/reservations/${segments[2]}` } : c,
    );
  }

  // Template preview: /office/templates/<type>/<templateId>/preview — a
  // template has no page of its own; it opens in its type's editor through
  // `?t=`, so its crumb points there. (`groups/<groupId>` is a real page.)
  if (
    segments[0] === "office" &&
    segments[1] === "templates" &&
    segments[2] !== "groups" &&
    segments.length > 3
  ) {
    crumbs = crumbs.map((c, i) =>
      i === 3
        ? { ...c, href: `/office/templates/${segments[2]}?t=${segments[3]}` }
        : c,
    );
  }

  // Conversations: `<app>/agent/conversations/<id>`, in every app that mounts
  // the agent — the `conversations` level has no page of its own (the menu
  // lives on the agent home), so point its crumb back at the agent instead of
  // a 404.
  if (segments[1] === "agent" && segments[2] === "conversations") {
    crumbs = crumbs.map((c, i) =>
      i === 2 ? { ...c, href: `/${segments[0]}/agent` } : c,
    );
  }

  if (given) {
    crumbs = given.map((c) => ({ ...c, verbatim: true }));
  }

  const lastIdx = crumbs.length - 1;

  return (
    <nav
      aria-label="Breadcrumb"
      className="label mb-2 flex flex-wrap items-center gap-x-1"
    >
      {crumbs.map((c, i) => {
        const isLast = i === lastIdx;
        // `currentLabel` overrides the last crumb verbatim (e.g. a SKU slug).
        const overridden = isLast && currentLabel != null;
        const verbatim = c.verbatim || overridden;
        return (
          // Indexed key: docs-reader crumbs can share an href (subdirectory
          // crumbs all point at the index).
          <span key={`${i}-${c.href}`} className="flex items-center gap-x-1">
            {i > 0 && (
              <span aria-hidden className="text-fg-subtle">
                /
              </span>
            )}
            {isLast ? (
              <span className={`text-fg ${verbatim ? "normal-case" : ""}`}>
                {overridden ? currentLabel : c.label}
              </span>
            ) : (
              <Link
                href={c.href}
                className={`transition-colors hover:text-fg ${
                  c.verbatim ? "normal-case" : ""
                }`}
              >
                {c.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
